import { ClickAwayListener } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Colors } from "../../Constants/colors";
import removeIcon from "../../images/remove.svg";
import editIcon from "../../images/edit-pen.svg";
import { createPortal } from "react-dom";

interface Props {
  visible: boolean;
  onClose: () => unknown;
  onRemove?: () => unknown;
  onEdit?: () => unknown;
  parentRef: React.RefObject<HTMLDivElement>;
}

interface optionAction {
  imageSource: string;
  text: string;
  isVisible: boolean;
  onClick: () => unknown;
}

export const MoreOptions = ({
  visible,
  onClose,
  onRemove,
  onEdit,
  parentRef,
}: Props) => {
  const divRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (parentRef.current) {
        const rect = parentRef.current.getBoundingClientRect();
        const x = rect.left + window.scrollX - 100;
        const y = rect.top + rect.height + window.scrollY + 10;
        setPosition({ x, y });
      }
    };

    const observer = new IntersectionObserver(
      () => {
        updatePosition(); // Recalculate position when parent intersects
      },
      { threshold: 1.0 }
    );

    if (parentRef.current) {
      observer.observe(parentRef.current);
    }

    return () => {
      if (parentRef.current) {
        observer.unobserve(parentRef.current);
      }
    };
  }, []);

  if (!visible) return <></>;

  const optionsAction: optionAction[] = [
    {
      imageSource: editIcon,
      text: "Edit",
      isVisible: !!onEdit,
      onClick: !!onEdit ? onEdit : () => {},
    },
    {
      imageSource: removeIcon,
      text: "Remove",
      isVisible: !!onRemove,
      onClick: !!onRemove ? onRemove : () => {},
    },
  ];

  const portalRoot = document.getElementById("app-root");

  if (!portalRoot) {
    return null; // or handle the absence of portalRoot as needed
  }

  return createPortal(
    <ClickAwayListener
      onClickAway={() => {
        onClose();
      }}
    >
      <div
        ref={divRef}
        className="flex flex-col"
        style={{
          padding: "16px",
          borderRadius: "2px",
          height: "auto",
          width: "120px",
          backgroundColor: Colors.White,
          position: "absolute",
          zIndex: 999,
          left: `${position.x}px`,
          top: `${position.y}px`,
          boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
          gap: 10,
          cursor: "pointer",
        }}
      >
        {optionsAction.map((action: optionAction) => {
          if (!action.isVisible) return null;

          return (
            <div
              key={action.text} // Add a unique key for list items
              className="flex flex-row items-center gap-x-3"
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                onClose();
              }}
            >
              <div style={{ width: "16px" }}>
                <img src={action.imageSource} alt={`${action.text}-icon`} />
              </div>
              <div>{action.text}</div>
            </div>
          );
        })}
      </div>
    </ClickAwayListener>,
    document.body
  );
};
